import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import type { EventJobItem } from "@/features/events/api/events.api";
import {
  formatEventClockTime,
  formatEventDate,
  formatPreciseDurationMs,
  getEventTimeline,
  type EventTimingStage,
} from "../../lib/event-timing";
import { EVENT_DETAIL_ROW_GRID, EventDetailSection } from "./EventDetailSection";

const STAGE_LABEL_KEY: Record<EventTimingStage["key"], string> = {
  clicked: "admin.events.drawer.clicked",
  received: "admin.events.drawer.received",
  published: "admin.events.drawer.published",
  started: "admin.events.drawer.started",
  completed: "admin.events.drawer.completed",
};

const STAGE_LABEL_FALLBACK: Record<EventTimingStage["key"], string> = {
  clicked: "Clicked",
  received: "Reached API",
  published: "Published",
  started: "Started",
  completed: "Completed",
};

export function EventTimingSection({
  event,
  now,
  stuckAfterMs,
}: {
  event: EventJobItem;
  now: number;
  /** Live PROCESSING duration past this reads as timed out. From the ops settings. */
  stuckAfterMs: number;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const stages = getEventTimeline(event, now, stuckAfterMs);
  const date = formatEventDate(event.publishedAt, locale);

  return (
    <EventDetailSection
      label={t("admin.events.drawer.timing", "Timing")}
      aside={date ? <span className="text-[11px] text-muted-foreground">{date}</span> : null}
    >
      <dl className="flex flex-col gap-1.5">
        {stages.map((stage) => {
          const clock = formatEventClockTime(stage.at, locale);

          return (
            <div key={stage.key} className={EVENT_DETAIL_ROW_GRID}>
              <dt className="text-xs leading-5 text-muted-foreground">
                {t(STAGE_LABEL_KEY[stage.key], STAGE_LABEL_FALLBACK[stage.key])}
              </dt>
              <dd
                className={cn(
                  "font-mono text-[11px] leading-5 tabular-nums",
                  clock ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {clock ?? "—"}
              </dd>
              <dd className="text-right text-[11px] leading-5 text-muted-foreground">
                <StageGap stage={stage} />
              </dd>
            </div>
          );
        })}
      </dl>
    </EventDetailSection>
  );
}

function StageGap({ stage }: { stage: EventTimingStage }) {
  const { t } = useTranslation();

  if (stage.sincePreviousMs == null) return null;

  const duration = formatPreciseDurationMs(stage.sincePreviousMs);
  const value = <span className="font-mono tabular-nums text-foreground">{duration}</span>;

  if (stage.isTimedOut) {
    return (
      <span className="text-error">
        {t("admin.events.drawer.gap.overdue", "over")} {duration}
      </span>
    );
  }
  if (stage.isRunning) {
    return <>{t("admin.events.drawer.gap.running", "running")} {value}</>;
  }
  if (stage.key === "received") {
    return <>{t("admin.events.drawer.gap.reached", "in transit")} {value}</>;
  }
  if (stage.key === "published") {
    return <>{t("admin.events.drawer.gap.prepared", "prepared")} {value}</>;
  }
  if (stage.key === "started") {
    return <>{t("admin.events.drawer.gap.waited", "waited")} {value}</>;
  }
  return <>{t("admin.events.drawer.gap.ran", "ran")} {value}</>;
}
